import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { TranscriptSpeakerAssignmentKind } from '@Application/Beneficiaries/Transcripts/Commands/AssignTranscriptSpeaker';
import AssignTranscriptSpeaker from '@Application/Beneficiaries/Transcripts/Commands/AssignTranscriptSpeaker';
import assignTranscriptSpeaker from '@Application/Beneficiaries/Transcripts/assignTranscriptSpeaker';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { setSpeakerAssignmentContext } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

export type AssignTranscriptSpeakerHandler = (
	kind: TranscriptSpeakerAssignmentKind,
	contactId: number | null,
	displayName: string | null,
	roleKey: string | null,
) => void;

/**
 * Shared by every choice that issues the same `PUT` — the beneficiary choice, a contact choice, and
 * the free-name field — so the dispatch, the closing gesture and the error swallow are written once.
 * Closes the panel on success, the same closing gesture the maquette's own `saveSpkFree` performs;
 * a rejection is left to `InterviewsNotifications` (T7), which is why it is swallowed here rather
 * than surfaced a second time.
 */
function useAssignTranscriptSpeaker(
	facilityId: number,
	subject: SubjectAddress,
	interviewId: number,
	speakerId: number,
): AssignTranscriptSpeakerHandler {
	const dispatch = useDispatch();

	return React.useCallback(
		(kind, contactId, displayName, roleKey) => {
			dispatch(
				assignTranscriptSpeaker(
					new AssignTranscriptSpeaker(
						facilityId,
						subject,
						interviewId,
						speakerId,
						kind,
						contactId,
						displayName,
						roleKey,
					),
				),
			)
				.then(() => setSpeakerAssignmentContext(null))
				.catch(ignoreHandledError);
		},
		[dispatch, facilityId, subject, interviewId, speakerId],
	);
}

export default useAssignTranscriptSpeaker;
